import React, { useEffect, useMemo, useRef, useState } from "react";

function AudioPlayer({
    src,
    className = "",
    audioClassName = "",
    onDownload = null,
    downloadLabel = "Download audio",
    onAddMarker = null,
    markerLabel = "Add note",
}) {
    const SPEED_CYCLE = [1, 1.2, 1.5, 2, 0.5];
    const audioRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [playbackRate, setPlaybackRate] = useState(1);

    useEffect(() => {
        const audioEl = audioRef.current;
        if (!audioEl) return undefined;

        const syncTime = () => {
            const now = Number.isFinite(audioEl.currentTime) ? audioEl.currentTime : 0;
            const total = Number.isFinite(audioEl.duration) ? audioEl.duration : 0;
            setCurrentTime(now);
            setDuration(total);
        };

        const onPlay = () => setIsPlaying(true);
        const onPause = () => setIsPlaying(false);
        const onEnded = () => setIsPlaying(false);

        audioEl.addEventListener("timeupdate", syncTime);
        audioEl.addEventListener("loadedmetadata", syncTime);
        audioEl.addEventListener("durationchange", syncTime);
        audioEl.addEventListener("play", onPlay);
        audioEl.addEventListener("pause", onPause);
        audioEl.addEventListener("ended", onEnded);
        syncTime();

        return () => {
            audioEl.removeEventListener("timeupdate", syncTime);
            audioEl.removeEventListener("loadedmetadata", syncTime);
            audioEl.removeEventListener("durationchange", syncTime);
            audioEl.removeEventListener("play", onPlay);
            audioEl.removeEventListener("pause", onPause);
            audioEl.removeEventListener("ended", onEnded);
        };
    }, []);

    useEffect(() => {
        const audioEl = audioRef.current;
        if (!audioEl) return;
        audioEl.pause();
        setIsPlaying(false);
        setCurrentTime(0);
        setDuration(0);
        setPlaybackRate(1);
        audioEl.playbackRate = 1;
    }, [src]);

    const currentLabel = useMemo(
        () => formatTime(currentTime),
        [currentTime]
    );
    const durationLabel = useMemo(
        () => formatTime(duration),
        [duration]
    );

    const togglePlayPause = async () => {
        const audioEl = audioRef.current;
        if (!audioEl) return;
        if (audioEl.paused) {
            try {
                await audioEl.play();
            } catch {
                setIsPlaying(false);
            }
            return;
        }
        audioEl.pause();
    };

    const seek = (deltaSeconds) => {
        const audioEl = audioRef.current;
        if (!audioEl) return;
        const current = Number.isFinite(audioEl.currentTime) ? audioEl.currentTime : 0;
        const duration = Number.isFinite(audioEl.duration) ? audioEl.duration : null;
        const target = Math.max(0, current + Number(deltaSeconds || 0));
        audioEl.currentTime = duration ? Math.min(target, duration) : target;
    };

    const handleSliderChange = (e) => {
        const audioEl = audioRef.current;
        if (!audioEl) return;
        const value = Number(e.target.value || 0);
        audioEl.currentTime = Math.max(0, value);
        setCurrentTime(Math.max(0, value));
    };

    const sliderMax = Number.isFinite(duration) && duration > 0 ? duration : 0;
    const hasMarkerAction = typeof onAddMarker === "function";
    const markerButtonLabel = String(markerLabel || "").trim() || "Add note";
    const setPlaybackRateOnAudio = (nextRate) => {
        const safeRate = SPEED_CYCLE.includes(nextRate) ? nextRate : 1;
        setPlaybackRate(safeRate);
        const audioEl = audioRef.current;
        if (audioEl) {
            audioEl.playbackRate = safeRate;
        }
    };
    const handleCyclePlaybackRate = () => {
        const currentIndex = SPEED_CYCLE.indexOf(playbackRate);
        const safeIndex = currentIndex >= 0 ? currentIndex : 0;
        const nextRate = SPEED_CYCLE[(safeIndex + 1) % SPEED_CYCLE.length];
        setPlaybackRateOnAudio(nextRate);
    };
    const handleAddMarker = () => {
        if (!hasMarkerAction) return;
        const audioEl = audioRef.current;
        if (!audioEl) return;
        audioEl.pause();
        const now = Number.isFinite(audioEl.currentTime) ? audioEl.currentTime : 0;
        const resumePlayback = async () => {
            try {
                await audioEl.play();
            } catch {
                // ignore autoplay/resume errors
            }
        };
        onAddMarker(Math.max(0, now), resumePlayback);
    };

    return (
        <div className={`audio-player ${className}`.trim()}>
            <div className={`audio-player__chrome ${audioClassName}`.trim()}>
                <div
                    className={
                        "audio-player__transport" +
                        (hasMarkerAction ? " audio-player__transport--with-marker" : "")
                    }
                >
                    <button
                        type="button"
                        className="btn btn--secondary audio-player__btn audio-player__btn--play"
                        onClick={togglePlayPause}
                        aria-label={isPlaying ? "Pause" : "Play"}
                        title={isPlaying ? "Pause" : "Play"}
                    >
                        <span
                            className="audio-player__play-glyph"
                            aria-hidden="true"
                        >
                            {isPlaying ? "❚❚" : "▶"}
                        </span>
                    </button>
                    <button
                        type="button"
                        className="btn btn--secondary audio-player__btn audio-player__btn--back"
                        onClick={() => seek(-10)}
                        aria-label="Back 10 seconds"
                        title="Back 10 seconds"
                    >
                        <span className="audio-player__btn-text">-10</span>
                    </button>
                    <button
                        type="button"
                        className="btn btn--secondary audio-player__btn audio-player__btn--forward"
                        onClick={() => seek(10)}
                        aria-label="Forward 10 seconds"
                        title="Forward 10 seconds"
                    >
                        <span className="audio-player__btn-text">+10</span>
                    </button>
                    <span className="audio-player__time audio-player__time--start">
                        {currentLabel}
                    </span>
                    <input
                        type="range"
                        className="audio-player__scrubber"
                        min={0}
                        max={sliderMax}
                        step={0.1}
                        value={Math.min(currentTime, sliderMax)}
                        onChange={handleSliderChange}
                    />
                    <span className="audio-player__time audio-player__time--end">
                        {durationLabel}
                    </span>
                    {hasMarkerAction ? (
                        <button
                            type="button"
                            className="btn btn--secondary audio-player__btn audio-player__btn--marker"
                            onClick={handleAddMarker}
                            aria-label={`${markerButtonLabel} at current playback position`}
                            title={markerButtonLabel}
                        >
                            {markerButtonLabel}
                        </button>
                    ) : null}
                    <button
                        type="button"
                        className="btn btn--secondary audio-player__btn audio-player__speed"
                        onClick={handleCyclePlaybackRate}
                        aria-label={`Playback speed ${playbackRate}x. Click to change speed.`}
                        title={`Playback speed ${playbackRate}x`}
                    >
                        {playbackRate}x
                    </button>
                    {typeof onDownload === "function" ? (
                        <button
                            type="button"
                            className="btn btn--secondary audio-player__btn audio-player__btn--download"
                            onClick={onDownload}
                            aria-label={downloadLabel}
                            title={downloadLabel}
                        >
                            <svg
                                className="audio-player__icon"
                                viewBox="0 0 16 16"
                                aria-hidden="true"
                            >
                                <path d="M8 2a.75.75 0 0 1 .75.75v5.44l1.72-1.72a.75.75 0 1 1 1.06 1.06L8.53 10.53a.75.75 0 0 1-1.06 0L4.47 7.53a.75.75 0 0 1 1.06-1.06l1.72 1.72V2.75A.75.75 0 0 1 8 2Z" />
                                <path d="M3.5 11.25a.75.75 0 0 1 .75.75v.5h7.5V12a.75.75 0 0 1 1.5 0v.75a1.25 1.25 0 0 1-1.25 1.25h-8A1.25 1.25 0 0 1 2.75 12.75V12a.75.75 0 0 1 .75-.75Z" />
                            </svg>
                        </button>
                    ) : null}
                </div>
            </div>
            <audio ref={audioRef} className="audio-player__native" src={src}>
                Your browser does not support the audio element.
            </audio>
        </div>
    );
}

function formatTime(seconds) {
    const total = Number.isFinite(seconds) ? Math.max(0, Math.floor(seconds)) : 0;
    const mins = Math.floor(total / 60);
    const secs = total % 60;
    return `${mins}:${String(secs).padStart(2, "0")}`;
}

export default AudioPlayer;
